import { format, getHours, getMinutes, setHours, setMinutes } from 'date-fns';
import React from 'react';
import { Pressable, View } from 'react-native';
import { useStyles } from 'react-native-unistyles';

import { Input } from '@/ui';
import { Calendar } from '@/ui/calendar';
import { Icon } from '@/ui/icon';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';

type Props = {
  id: string;
  label: string;
  value: Date | null;
  minDate?: Date;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onChange: (date: Date) => void;
};

// Read-only input that opens a datetime calendar popover, following the
// pattern used by the partner block-car modal.
export const DatetimeField = ({
  id,
  label,
  value,
  minDate,
  isOpen,
  onToggle,
  onClose,
  onChange,
}: Props) => {
  const { theme } = useStyles();

  const clampToMin = (date: Date) => {
    if (minDate && date < minDate) {
      return minDate;
    }

    return date;
  };

  return (
    <View style={{ position: 'relative' }}>
      <Pressable onPress={onToggle}>
        <Input
          label={label}
          value={value ? format(value, 'MM/dd/yyyy h:mm a') : ''}
          placeholder="Select date and time"
          editable={false}
          pointerEvents="none"
          leftIcon={
            <Icon
              type="naked"
              icon={<CalendarIcon />}
              fillColor={theme.colors.icon.color}
              width={22}
              height={22}
            />
          }
        />
      </Pressable>

      <Calendar
        mode="datetime"
        isVisible={isOpen}
        minDate={minDate}
        defaultStartDate={value}
        onHide={onClose}
        onDatesSelected={(date) => {
          onChange(clampToMin(date));
          onClose();
        }}
        onDateSelected={(date) => {
          // Keep the previously selected time when only the date changes.
          const current = value ?? new Date();
          const withTime = setMinutes(
            setHours(date, getHours(current)),
            getMinutes(current)
          );

          onChange(clampToMin(withTime));
        }}
        onTimeSelected={(date) => {
          onChange(clampToMin(date));
          onClose();
        }}
        range={false}
        id={id}
        inline
        style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          zIndex: 9999,
        }}
        containerStyle={{
          width: 500,
          borderWidth: 1,
          borderColor: theme.colors.card.default.borderColor,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.3,
          shadowRadius: 12,
          elevation: 20,
        }}
      />
    </View>
  );
};
